import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Pie } from 'react-chartjs-2';
import './BrokerDashboard.css'; // Ensure your CSS file is correctly linked and updated if needed
import api from '../services/api';
// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend);

console.log(api.defaults.baseURL)
const API_BROKER_FORMS_URL = api.defaults.baseURL + "/broker/assigned-forms";
// --- KPI Card Component ---
const KpiCard = ({ title, value, icon, status }) => (
    <div className={`kpi-card-compact status-${status}`}>
        <div className="kpi-icon-compact">{icon}</div>
        <div className="kpi-content-compact">
            <div className="kpi-value-compact">{value}</div>
            <div className="kpi-title-compact">{title}</div>
        </div>
    </div>
);

// --- Form Item Component ---
const FormItem = ({ form, onDelete }) => {
    // Determine the form's state for conditional rendering
    const isPending = form.status === 'pending';
    const isFinalized = form.status === 'finalized';
    const isSubmitted = form.status === 'submitted'; // Added for clarity

    const formattedSubmittedDate = form.lastSubmissionAt 
        ? new Date(form.lastSubmissionAt).toLocaleDateString() 
        : 'N/A';

    return (
        <div className="form-item-compact">
            {/* Status dot visual indicator */}
            <div className={`status-dot status-${form.status}`}></div>
            
            <div className="form-details-compact">
                <span className="form-name-compact">
                    {form.templateSnapshot.templateName} | Lot: {form.lotNumber || 'N/A'}
                </span>
                <span className="form-meta-compact">
                    {isPending
                        ? `Expires: ${form.expiresAt ? new Date(form.expiresAt).toLocaleDateString() : 'N/A'}`
                        : `Value: ${new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(form.grandTotal || 0)} | Submitted: ${formattedSubmittedDate} `
                    }
                </span>
            </div>
            
            <div className="form-actions-compact">
                {/* Link to view/fill the form, with dynamic styling and text */}
                <Link 
                    to={`/broker/form/${form._id}`} 
                    className={`btn btn-small 
                                ${isPending ? 'btn-primary' : ''} 
                                ${isSubmitted ? 'btn-secondary' : ''}
                                ${isFinalized ? 'btn-success' : ''}
                                `}
                >
                    {isPending ? 'Fill' : (isFinalized ? 'View Final' : 'View')}
                </Link>
                {/* Delete button (currently commented out) */}
                {/* <button onClick={() => onDelete(form._id)} className="btn-delete" title="Delete Form">🗑️</button> */}
            </div>
        </div>
    );
};

// --- Analytics Chart Component ---
const AnalyticsChart = ({ data }) => {
    const chartData = {
        labels: ['Pending', 'Submitted', 'Finalized', 'Expired'], // Added Finalized to labels
        datasets: [{
            label: 'Form Status',
            data: [data.pending, data.submitted, data.finalized, data.expired], // Added finalized data
            backgroundColor: [
                'rgba(243, 156, 18, 0.8)',   // Pending (Orange)
                'rgba(76, 175, 80, 0.8)',    // Submitted (Green)
                'rgba(52, 152, 219, 0.8)',   // Finalized (Blue)
                'rgba(149, 165, 166, 0.8)'   // Expired (Grey)
            ],
            borderColor: [
                '#f39c12',
                '#4CAF50',
                '#3498db',
                '#95a5a6'
            ],
            borderWidth: 1,
        }],
    };

    const options = {
        responsive: true,
        plugins: {
            legend: {
                position: 'top',
            },
            tooltip: {
                callbacks: {
                    label: function (context) {
                        return `${context.label}: ${context.raw} forms`;
                    }
                }
            }
        }
    };

    return <Pie data={chartData} options={options} />;
};


// --- Main BrokerDashboard Component ---
const BrokerDashboard = () => {
    const [forms, setForms] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Memoized user data from localStorage for performance and reusability
    const userData = useMemo(() => {
        const storedUserJSON = localStorage.getItem('user');
        let user = { name: 'Guest', email: 'N/A', phoneNumber: 'N/A' }; // Initialize with defaults
        let fieldsCompleted = 0;
        const fieldsToCheck = ['name', 'email', 'phoneNumber']; // Ensure 'phoneNumber' matches your user object key

        if (storedUserJSON) {
            try {
                const parsedUser = JSON.parse(storedUserJSON);
                // Merge parsed user data with defaults, so we don't overwrite if a field is missing
                user = { ...user, ...parsedUser }; 

                fieldsToCheck.forEach(field => {
                    if (user[field]) fieldsCompleted++;
                });
            } catch (e) {
                console.error("Failed to parse user data from localStorage", e);
            }
        }

        const profileCompleteness = Math.round((fieldsCompleted / fieldsToCheck.length) * 100);

        return {
            name: user.name || 'Broker',
            email: user.email || 'N/A',
            phone: user.phoneNumber || 'N/A', // Use phoneNumber from user object
            profileCompleteness: profileCompleteness,
        };
    }, []);

    // Effect to fetch broker forms when the component mounts
    useEffect(() => {
        const fetchBrokerForms = async () => {
            setIsLoading(true);
            setError(null);
            const token = localStorage.getItem('authToken');

            if (!token) {
                setError("Authentication Error: Please log back in.");
                setIsLoading(false);
                return;
            }

            try {
                const response = await fetch(API_BROKER_FORMS_URL, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (!response.ok) {
                    const errorText = await response.text(); // Get raw error text
                    throw new Error(`Server Error: ${response.status} - ${errorText}`);
                }
                const data = await response.json();
                console.log("Fetched API Forms Data:", data); // For debugging
                setForms(data);
            } catch (e) {
                console.error("Error fetching broker forms:", e); // For debugging
                setError(e.message || "Failed to load forms.");
            } finally {
                setIsLoading(false);
            }
        };
        fetchBrokerForms();
    }, []); // Empty dependency array means this runs once on mount

    // Function to handle form deletion (if enabled)
    const handleDeleteForm = async (formId) => {
        if (!window.confirm("Are you sure you want to delete this form? This action cannot be undone.")) return;
        const token = localStorage.getItem('authToken');
        if (!token) {
            alert("Authentication token missing. Please log in again.");
            return;
        }

        try {
            const response = await fetch(`${API_BROKER_FORMS_URL}/${formId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) {
                const errData = await response.json();
                throw new Error(errData.message || `Failed to delete form ${formId}.`);
            }
            setForms(currentForms => currentForms.filter(form => form._id !== formId));
            alert("Form deleted successfully!");
        } catch (e) {
            console.error("Error deleting form:", e);
            alert(`Error: ${e.message}`);
        }
    };

    // Memoized filtering of forms into categories for display and analytics
    const { pendingForms, submittedForms, expiredForms, finalizedForms, totalValue } = useMemo(() => {
        const now = new Date();
        const pending = forms.filter(f => f.status === 'pending' && (!f.expiresAt || new Date(f.expiresAt) > now));
        const submitted = forms.filter(f => f.status === 'submitted');
        const finalized = forms.filter(f => f.status === 'finalized'); // Filter for finalized forms
        const expired = forms.filter(f => f.status === 'pending' && f.expiresAt && new Date(f.expiresAt) <= now);
        
        // Calculate total value only for submitted forms
        const value = submitted.reduce((sum, form) => sum + (form.grandTotal || 0), 0);

        console.log("Filtered Forms - Pending:", pending.length); // Debugging
        console.log("Filtered Forms - Submitted:", submitted.length); // Debugging
        console.log("Filtered Forms - Finalized:", finalized.length); // Debugging
        console.log("Filtered Forms - Expired:", expired.length); // Debugging

        return { pendingForms: pending, submittedForms: submitted, expiredForms: expired, finalizedForms: finalized, totalValue: value };
    }, [forms]); // Recalculate whenever 'forms' state changes

    // Main render block
    return (
        <div className="broker-dashboard-page">
            <header className="dashboard-header">
                <div>
                    <h1>Welcome back, {userData.name}!</h1>
                    <p>Here is your current sales and forms overview.</p>
                </div>
            </header>

            {/* Loading and Error States */}
            {isLoading && <div className="card"><h2>Loading Dashboard...</h2></div>}
            {error && <div className="card error-card"><h2>Error</h2><p>{error}</p></div>}

            {/* Dashboard Content (only shown when not loading and no error) */}
            {!isLoading && !error && (
                <>
                    {/* KPI Grid Section */}
                    <section className="kpi-grid">
                        <KpiCard title="Pending Forms" value={pendingForms.length} icon="📝" status="pending" />
                        <KpiCard title="Submitted Forms" value={submittedForms.length} icon="✅" status="submitted" />
                        <KpiCard title="Finalized Forms" value={finalizedForms.length} icon="🏅" status="finalized" /> {/* New KPI Card */}
                        <KpiCard title="Total Submitted Value" value={new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(totalValue)} icon="💰" status="submitted" />
                        <KpiCard title="Expired Forms" value={expiredForms.length} icon="⏳" status="expired" />
                    </section>

                    <main className="dashboard-layout">
                        <div className="dashboard-main-content">
                            {/* Action Required (Pending Forms) Section */}
                            <div className="card">
                                <h3 className="card-title">Action Required</h3>
                                <div className="form-list-compact">
                                    {pendingForms.length > 0 ? (
                                        pendingForms.map(form => <FormItem key={form._id} form={form} onDelete={handleDeleteForm} />)
                                    ) : <p className="empty-state">No pending forms. Great job!</p>}
                                </div>

                                <hr className="divider" />

                                {/* Submission History Section */}
                                <h3 className="card-title">Submission History</h3>
                                <div className="form-list-compact">
                                    {submittedForms.length > 0 ? (
                                        submittedForms.map(form => <FormItem key={form._id} form={form} onDelete={handleDeleteForm} />)
                                    ) : <p className="empty-state">No submitted forms yet.</p>}
                                </div>
                                
                                <hr className="divider" />

                                {/* Finalized History Section (NEW) */}
                                <h3 className="card-title">Finalized History</h3> 
                                <div className="form-list-compact">
                                    {finalizedForms.length > 0 ? (
                                        finalizedForms.map(form => <FormItem key={form._id} form={form} onDelete={handleDeleteForm} />)
                                    ) : <p className="empty-state">No finalized forms yet.</p>}
                                </div>
                            </div>
                        </div>

                        {/* Sidebar Content */}
                        <aside className="dashboard-sidebar-content">
                            {/* Status Overview Chart */}
                            <div className="card">
                                <h3 className="card-title">Status Overview</h3>
                                <AnalyticsChart data={{ pending: pendingForms.length, submitted: submittedForms.length, expired: expiredForms.length, finalized: finalizedForms.length }} />
                            </div>
                            {/* Profile Information */}
                            <div className="card">
                                <h3 className="card-title">Profile</h3>
                                <p className="profile-agency">Name: <strong>{userData.name}</strong></p>
                                <p className="profile-agency">Phone: <strong>{userData.phone}</strong></p>
                                <p className="profile-agency">Email: <strong>{userData.email}</strong></p>
                            </div>
                        </aside>
                    </main>
                </>
            )}
        </div>
    );
};

export default BrokerDashboard;